import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Volume2, Server, Copy } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { SoundGrid } from '../organisms/SoundGrid';
import { UploadSoundDialog } from '../organisms/UploadSoundDialog';
import { useSoundManagementStore } from '../../stores/sound-management-store';
import { useApplicationLanguagesStore } from '../../stores/application-languages-store';
import { useTranslationStore } from '../../stores/translation-store';
import {
  ApplicationType,
  ApplicationTypeLabels,
} from '../../types/translation-types';
import {
  MediaItem,
  UploadSoundRequest,
  supportsSounds,
  SOUND_SUPPORTED_APP_TYPES,
} from '../../types/sound-management-types';
import { parseCompoundLanguageCode } from '../../utils/language-code';
import { toast } from 'sonner';

export function SoundManagement() {
  const navigate = useNavigate();

  // Sound management store
  const {
    sounds,
    totalCount,
    currentPage,
    pageSize,
    applicationType,
    languageFilter,
    isLoading,
    isUploading,
    isDeleting,
    isCopyingSounds,
    error,
    uploadError,
    copySoundsError,
    uploadDialogOpen,
    deleteDialogOpen,
    soundToDelete,
    copySoundsDialogOpen,
    playingMediaId,
    fetchSounds,
    uploadSound,
    deleteSound,
    copySoundsFromParent,
    setApplicationType,
    setLanguageFilter,
    setCurrentPage,
    openUploadDialog,
    closeUploadDialog,
    openDeleteDialog,
    closeDeleteDialog,
    openCopySoundsDialog,
    closeCopySoundsDialog,
    setPlayingMediaId,
  } = useSoundManagementStore();

  // Get available languages for the selected app type
  const {
    applicationLanguages,
    fetchApplicationLanguages,
  } = useApplicationLanguagesStore();

  // Sync with shared app type from translation store
  const { selectedAppType: sharedAppType, setSelectedAppType: setSharedAppType } = useTranslationStore();

  // Initialize app type from shared store if it supports sounds
  useEffect(() => {
    if (sharedAppType && supportsSounds(sharedAppType) && sharedAppType !== applicationType) {
      setApplicationType(sharedAppType);
    }
  }, [sharedAppType, applicationType, setApplicationType]);

  // Fetch sounds when filters change
  useEffect(() => {
    if (applicationType) {
      fetchSounds();
    }
  }, [fetchSounds, applicationType, languageFilter, currentPage]);

  // Fetch available languages when app type changes
  useEffect(() => {
    if (applicationType) {
      fetchApplicationLanguages(applicationType);
    }
  }, [fetchApplicationLanguages, applicationType]);

  const handleAppTypeChange = useCallback(
    (value: string) => {
      const appType = parseInt(value) as ApplicationType;
      setApplicationType(appType);
      setSharedAppType(appType);
    },
    [setApplicationType, setSharedAppType]
  );

  const handleLanguageFilterChange = useCallback(
    (value: string) => {
      setLanguageFilter(value === 'all' ? null : value);
    },
    [setLanguageFilter]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleUploadClick = useCallback(() => {
    openUploadDialog();
  }, [openUploadDialog]);

  const handleUpload = useCallback(
    async (request: UploadSoundRequest, skipRefresh = false) => {
      await uploadSound(request, skipRefresh);
      if (!skipRefresh) {
        toast.success('Sound uploaded successfully');
      }
    },
    [uploadSound]
  );

  const handleBatchUploadComplete = useCallback(() => {
    fetchSounds();
    closeUploadDialog();
    toast.success('Sound files uploaded successfully');
  }, [fetchSounds, closeUploadDialog]);

  const handleDeleteClick = useCallback(
    (sound: MediaItem) => {
      openDeleteDialog(sound);
    },
    [openDeleteDialog]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!soundToDelete) return;

    const success = await deleteSound(soundToDelete.mediaId);
    if (success) {
      toast.success(`Sound "${soundToDelete.originalFileName}" deleted successfully`);
    }
  }, [soundToDelete, deleteSound]);

  const handlePlay = useCallback(
    (sound: MediaItem) => {
      setPlayingMediaId(sound.mediaId);
    },
    [setPlayingMediaId]
  );

  const handleStop = useCallback(() => {
    setPlayingMediaId(null);
  }, [setPlayingMediaId]);

  const handleRetry = useCallback(() => {
    fetchSounds();
  }, [fetchSounds]);

  // "Copy from parent" is only meaningful when the current language filter
  // is a per-operator compound code (e.g. "he#paz"), since only those have a parent.
  const compoundLanguage = useMemo(
    () => (languageFilter ? parseCompoundLanguageCode(languageFilter) : null),
    [languageFilter]
  );
  const canCopyFromParent = !!compoundLanguage?.operatorName;

  const handleCopySoundsClick = useCallback(() => {
    openCopySoundsDialog();
  }, [openCopySoundsDialog]);

  const handleConfirmCopySounds = useCallback(async () => {
    if (!languageFilter || !compoundLanguage?.operatorName) return;

    try {
      const result = await copySoundsFromParent(languageFilter, compoundLanguage.baseCode);
      toast.success(`Copied ${result.copiedCount} sound file(s) from ${compoundLanguage.baseCode}`);
      closeCopySoundsDialog();
    } catch {
      toast.error(`Failed to copy sound files from ${compoundLanguage.baseCode}`);
    }
  }, [languageFilter, compoundLanguage, copySoundsFromParent, closeCopySoundsDialog]);

  // Convert application languages to the format expected by the upload dialog
  const availableLanguages = applicationLanguages.map((lang) => ({
    languageCode: lang.languageCode,
    languageName: lang.languageName,
  }));

  return (
    <div className="flex flex-col h-full min-h-screen overflow-hidden">
      {/* Header - fixed height */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/translations')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Sound Management</h1>
              <p className="text-muted-foreground">
                Manage audio files for Charger and Kiosk applications
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {canCopyFromParent && (
              <Button variant="outline" onClick={handleCopySoundsClick}>
                <Copy className="h-4 w-4 mr-2" />
                Copy from parent
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/translations/publish')}>
              <Server className="h-4 w-4 mr-2" />
              Publish
            </Button>
            <Button onClick={handleUploadClick}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Sound
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Application Type */}
          <div className="space-y-1">
            <Label htmlFor="appType" className="text-xs text-muted-foreground">
              Application
            </Label>
            <Select
              value={applicationType.toString()}
              onValueChange={handleAppTypeChange}
            >
              <SelectTrigger id="appType" className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOUND_SUPPORTED_APP_TYPES.map((type) => (
                  <SelectItem key={type} value={type.toString()}>
                    {ApplicationTypeLabels[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language Filter */}
          <div className="space-y-1">
            <Label htmlFor="languageFilter" className="text-xs text-muted-foreground">
              Language
            </Label>
            <Select
              value={languageFilter || 'all'}
              onValueChange={handleLanguageFilterChange}
            >
              <SelectTrigger id="languageFilter" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang.languageCode} value={lang.languageCode}>
                    {lang.languageName} ({lang.languageCode})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sound count */}
          <div className="flex items-center gap-2 ml-auto text-sm text-muted-foreground">
            <Volume2 className="h-4 w-4" />
            <span>{totalCount} sound{totalCount !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <strong>Note:</strong> Sound files uploaded here will be included in the published translation JSON
          when you publish translations for the selected application.
        </div>
      </div>

      {/* Grid - flex-1 to fill remaining space */}
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-4">
        <SoundGrid
          sounds={sounds}
          isLoading={isLoading}
          error={error}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          playingMediaId={playingMediaId}
          groupByLanguage={!languageFilter}
          onPageChange={handlePageChange}
          onPlay={handlePlay}
          onStop={handleStop}
          onDelete={handleDeleteClick}
          onRetry={handleRetry}
        />
      </div>

      {/* Upload Dialog */}
      <UploadSoundDialog
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        applicationType={applicationType}
        availableLanguages={availableLanguages}
        onUpload={handleUpload}
        onRefresh={handleBatchUploadComplete}
        isUploading={isUploading}
        error={uploadError}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sound</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the sound file{' '}
              <strong>"{soundToDelete?.originalFileName}"</strong>? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Sounds From Parent Confirmation Dialog */}
      <AlertDialog
        open={copySoundsDialogOpen}
        onOpenChange={(open) => !open && closeCopySoundsDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copy Sound Files From Parent</AlertDialogTitle>
            <AlertDialogDescription>
              Copy all sound files from{' '}
              <strong className="font-mono">{compoundLanguage?.baseCode}</strong> to{' '}
              <strong className="font-mono">{languageFilter}</strong> across all application
              types? Sounds that {languageFilter} already has of its own will be skipped.
              {copySoundsError && (
                <span className="block mt-2 text-red-600">{copySoundsError}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCopyingSounds}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCopySounds} disabled={isCopyingSounds}>
              {isCopyingSounds ? 'Copying...' : 'Copy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
